import React from 'react';
import { ICommissionModelLevel } from './types';
import { CLabel, CInput, CButton, CInputGroup, CInputGroupPrepend, CInputGroupText } from '@coreui/react';
import _t from 'counterpart';
import CIcon from '@coreui/icons-react';
import { onlyNonNegativeDecimal, onlyPositiveInteger } from '../../helpers';

interface IProps {
  index: number;
  commissionLevel: ICommissionModelLevel;
  handleInputChange: (index: number, updatedLevel: ICommissionModelLevel) => void;
  onLevelRemoved: (index: number) => void;
}

const CommissionLevelForm = ({ index, commissionLevel, handleInputChange, onLevelRemoved }: IProps) => {
  const {
    level,
    cpaAmount,
    volumeCommissionAmount,
    profitSharePercentage,
    propPurchaseFirstPercentage,
    propPurchaseSubsequentPercentage,
  } = commissionLevel;

  const onLevelChanged = (e: React.FormEvent<any>) => {
    const target = e.target as HTMLInputElement | HTMLSelectElement;
    const value = target.value;

    if (onlyPositiveInteger(value)) {
      const newCommissionLevel = { ...commissionLevel };
      newCommissionLevel.level = value ? Number(value) : null;
      handleInputChange(index, newCommissionLevel);
    }
  };

  const onCpaAmountChange = (e: React.FormEvent<any>) => {
    const target = e.target as HTMLInputElement | HTMLSelectElement;
    const value = target.value;

    if (onlyNonNegativeDecimal(value)) {
      const newCommissionLevel = { ...commissionLevel };
      newCommissionLevel.cpaAmount = value ? Number(value) : null;
      newCommissionLevel.cpaCurrency = 'USD';
      handleInputChange(index, newCommissionLevel);
    }
  };

  const onVolumeCommissionAmountChange = (e: React.FormEvent<any>) => {
    const target = e.target as HTMLInputElement | HTMLSelectElement;
    const value = target.value;

    if (onlyNonNegativeDecimal(value)) {
      const newCommissionLevel = { ...commissionLevel };
      newCommissionLevel.volumeCommissionAmount = value ? Number(value) : null;
      newCommissionLevel.volumeCommissionCurrency = 'USD';
      handleInputChange(index, newCommissionLevel);
    }
  };

  const onProfitPercentage = (e: React.FormEvent<any>) => {
    const target = e.target as HTMLInputElement | HTMLSelectElement;
    const value = target.value;

    if (onlyNonNegativeDecimal(value)) {
      const newCommissionLevel = { ...commissionLevel };
      newCommissionLevel.profitSharePercentage = value ? Number(value) : null;
      handleInputChange(index, newCommissionLevel);
    }
  };

  const onPropPurchaseFirstPercentage = (e: React.FormEvent<any>) => {
    const target = e.target as HTMLInputElement | HTMLSelectElement;
    const value = target.value;

    if (onlyNonNegativeDecimal(value)) {
      const newCommissionLevel = { ...commissionLevel };
      newCommissionLevel.propPurchaseFirstPercentage = value ? Number(value) : null;
      handleInputChange(index, newCommissionLevel);
    }
  };

  const onPropPurchaseSubsequentPercentage = (e: React.FormEvent<any>) => {
    const target = e.target as HTMLInputElement | HTMLSelectElement;
    const value = target.value;

    if (onlyNonNegativeDecimal(value)) {
      const newCommissionLevel = { ...commissionLevel };
      newCommissionLevel.propPurchaseSubsequentPercentage = value ? Number(value) : null;
      handleInputChange(index, newCommissionLevel);
    }
  };

  const removeLevel = () => {
    onLevelRemoved(index);
  };

  return (
    <div className="mt-2">
      <div className="d-flex flex-row align-items-end mb-3 flex-wrap">
        <div className="col-12 col-md-3 mb-2 mb-md-0">
          <CLabel htmlFor="level">{_t('global.level')}</CLabel>
          <CInput type="number" name="level" placeholder="Level" value={level?.toString()} onChange={onLevelChanged} />
        </div>
        <div className="col-12 col-md-3 mb-2 mb-md-0">
          <CLabel htmlFor="cpaAmount">{_t('commissions.cost-per-action')}</CLabel>
          <CInputGroup>
            <CInputGroupPrepend>
              <CInputGroupText>$</CInputGroupText>
            </CInputGroupPrepend>
            <CInput
              type="number"
              name="cpaAmount"
              placeholder="2.55"
              value={cpaAmount?.toString()}
              onChange={onCpaAmountChange}
            />
          </CInputGroup>
        </div>
        <div className="col-12 col-md-3 mb-2 mb-md-0">
          <CLabel htmlFor="profitSharePercentage">{_t('commissions.profit-share-percentage')}</CLabel>
          <CInputGroup>
            <CInputGroupPrepend>
              <CInputGroupText>%</CInputGroupText>
            </CInputGroupPrepend>
            <CInput
              type="number"
              name="profitSharePercentage"
              placeholder="2.55"
              value={profitSharePercentage?.toString() || ''}
              onChange={onProfitPercentage}
            />
          </CInputGroup>
        </div>
        <div className="col-12 col-md-3 mb-2 mb-md-0">
          <CLabel htmlFor="volumeCommissionAmount">{_t('commissions.volume-commission-amount')}</CLabel>
          <CInputGroup>
            <CInputGroupPrepend>
              <CInputGroupText>$</CInputGroupText>
            </CInputGroupPrepend>
            <CInput
              type="number"
              name="volumeCommissionAmount"
              placeholder="2.55"
              value={volumeCommissionAmount?.toString() || ''}
              onChange={onVolumeCommissionAmountChange}
            />
          </CInputGroup>
        </div>
      </div>
      <div className="d-flex flex-row align-items-end flex-wrap">
        <div className="col-12 col-md-3 mb-2 mb-md-0">{/* Empty space */}</div>
        <div className="col-12 col-md-3 mb-2 mb-md-0">
          <CLabel htmlFor="propPurchaseFirstPercentage">{_t('commissions.prop-purchase-first-percentage')}</CLabel>
          <CInputGroup>
            <CInputGroupPrepend>
              <CInputGroupText>%</CInputGroupText>
            </CInputGroupPrepend>
            <CInput
              type="number"
              name="propPurchaseFirstPercentage"
              placeholder="5.0"
              value={propPurchaseFirstPercentage?.toString() || ''}
              onChange={onPropPurchaseFirstPercentage}
            />
          </CInputGroup>
        </div>
        <div className="col-12 col-md-3 mb-2 mb-md-0">
          <CLabel htmlFor="propPurchaseSubsequentPercentage">
            {_t('commissions.prop-purchase-subsequent-percentage')}
          </CLabel>
          <CInputGroup>
            <CInputGroupPrepend>
              <CInputGroupText>%</CInputGroupText>
            </CInputGroupPrepend>
            <CInput
              type="number"
              name="propPurchaseSubsequentPercentage"
              placeholder="3.0"
              value={propPurchaseSubsequentPercentage?.toString() || ''}
              onChange={onPropPurchaseSubsequentPercentage}
            />
          </CInputGroup>
        </div>
        <div className="col-12 col-md-3 d-flex align-items-end justify-content-start justify-content-md-end">
          <CButton size="sm" color="danger" onClick={removeLevel}>
            <CIcon name="cil-trash" size="sm" />
          </CButton>
        </div>
      </div>
    </div>
  );
};

export default CommissionLevelForm;
